import React, { useEffect, useRef, useState } from "react";
import { Clock } from "lucide-react";
import PickerPopover from "./PickerPopover";

/**
 * Selector de hora propio, en lugar del nativo del navegador: el nativo no se
 * puede estilar ni se cierra solo al elegir.
 *
 * Se cierra en cuanto se ha elegido hora y minutos.
 */

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));

/** Minutos de 5 en 5: 00, 05, 10 … 55. */
const MINUTE_STEP = 5;
const MINUTES = Array.from(
  { length: 60 / MINUTE_STEP },
  (_, i) => String(i * MINUTE_STEP).padStart(2, "0")
);

/**
 * Si el valor que llega no cae en el paso (por ejemplo "10:15"), lo insertamos
 * en su sitio: así el usuario ve marcado lo que realmente tiene puesto.
 */
const minuteOptions = (current) => {
  if (!current || MINUTES.includes(current)) return MINUTES;
  const n = Number(current);
  if (!Number.isInteger(n) || n < 0 || n > 59) return MINUTES;
  return [...MINUTES, current].sort((a, b) => Number(a) - Number(b));
};

function Column({ items, selected, onPick, label, colRef }) {
  return (
    <div className="dyd-tp-col" role="listbox" aria-label={label} ref={colRef}>
      {items.map((item) => (
        <button
          key={item}
          type="button"
          role="option"
          aria-selected={item === selected}
          data-selected={item === selected}
          className={`dyd-tp-item ${item === selected ? "is-selected" : ""}`}
          onClick={() => onPick(item)}
        >
          {item}
        </button>
      ))}
    </div>
  );
}

function TimePicker({ value = "", onChange, id }) {
  const [open, setOpen] = useState(false);
  const [touched, setTouched] = useState({ hour: false, minute: false });
  const anchorRef = useRef(null);
  const hourColRef = useRef(null);
  const minuteColRef = useRef(null);

  const [hour = "", minute = ""] = value ? value.split(":") : [];

  // Al abrir, dejar a la vista lo que ya está elegido
  useEffect(() => {
    if (!open) return;
    // Se mueve solo la columna: scrollIntoView también desplaza la página o el
    // modal que hay detrás, y en el móvil el campo se iba fuera de la vista
    [hourColRef, minuteColRef].forEach((ref) => {
      const col = ref.current;
      const selected = col?.querySelector('[data-selected="true"]');
      if (!col || !selected) return;
      col.scrollTop = selected.offsetTop - col.offsetTop - (col.clientHeight - selected.offsetHeight) / 2;
    });
  }, [open]);

  const toggle = () => {
    setTouched({ hour: false, minute: false });
    setOpen((v) => !v);
  };

  const pick = (part) => (next) => {
    const h = part === "hour" ? next : hour || "00";
    const m = part === "minute" ? next : minute || "00";
    onChange(`${h}:${m}`);

    // Se cierra cuando se ha tocado una columna y la otra
    const after = { ...touched, [part]: true };
    setTouched(after);
    if (after.hour && after.minute) setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        id={id}
        ref={anchorRef}
        onClick={toggle}
        className={`dyd-picker-field ${open ? "is-open" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Clock size={15} />
        <span className={value ? "" : "dyd-picker-placeholder"}>{value || "--:--"}</span>
      </button>

      <PickerPopover anchorRef={anchorRef} open={open} onClose={() => setOpen(false)}>
        <div className="dyd-tp-cols">
          <Column
            items={HOURS}
            selected={hour}
            onPick={pick("hour")}
            label="Hora"
            colRef={hourColRef}
          />
          <span className="dyd-tp-sep">:</span>
          <Column
            items={minuteOptions(minute)}
            selected={minute}
            onPick={pick("minute")}
            label="Minutos"
            colRef={minuteColRef}
          />
        </div>
      </PickerPopover>
    </>
  );
}

export default TimePicker;
